import { catchError, debounce, noop, pipe, when } from 'overmind';
import { compose, not } from 'ramda';

import type { ErrorWithDetails } from '@/app/components/App/CollapsibleError';
import type { Context } from '@/overmind';
import { GridElementType } from '@/overmind/effects/api/gridElementDetails';
import type { ContainmentResultWithExecutionTime } from '@/overmind/effects/api/networkServiceApi';
import {
  getContainment,
  withExecutionTime
} from '@/overmind/effects/api/networkServiceApi';
import type { GUID } from '@/overmind/effects/auth';
import { AbortError, UnexpectedStateError } from '@/overmind/errors';

import type {
  CalculationTactic,
  CompletedCalculationTactic,
  DomainGuid,
  FailedCalculationTactic,
  GridElementSectionWithQuickAccessResult
} from '.';
import type { CalculationState } from './createCalculationState';
import type { GridElementsDetailsByCalculationType } from './derived-state/gridElementsDetailsByCalculationType/GridElementsDetails';
import { type GridElementPropertyAttentionStatusByCalculationType } from './evaluation/AttentionStatusRepository';
import { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

export type RunCalculationV2Action = {
  id: DomainGuid;
  tacticId: string;
};

export function toErrorWithDetailsFromCalculationSetExecutionError(
  calculationTactic: Extract<
    CalculationState['calculationTactic'],
    { current: 'ERROR' }
  >
): ErrorWithDetails {
  if (calculationTactic.current === 'ERROR') {
    const error = calculationTactic.error as any; // FIXME: Casting to any as it's a hack for supporting more than just string error
    let mainMessage = '';
    let details = '';

    if (error?.errorMessage) {
      mainMessage = error.errorMessage as string;
    }

    if (error?.details) {
      details = error.details as string;
    }

    return { mainMessage, details };
  }
  return { mainMessage: 'Calculation set execution failed.', details: '' };
}

export function toErrorWithDetailsFromLoadedCalculationSet(
  calculationTactic: Extract<
    CalculationState['calculationTactic'],
    { current: 'LOADED' }
  >
): ErrorWithDetails {
  if (calculationTactic.value.status === 'Failed') {
    const error = calculationTactic.value.details as any; // FIXME: Casting to any as it's a hack for supporting more than just string error
    const mainMessage = takeFirstDetailsStatement(error);
    const details = takeEverythingWithoutFirstDetailsStatement(error);

    return { mainMessage, details };
  }
  return { mainMessage: 'Calculation set execution failed.', details: '' };
}

export function toErrorMessage(
  calculationTactic: Extract<
    CalculationState['calculationTactic'],
    { current: 'ERROR' }
  >
): string {
  if (calculationTactic.current === 'ERROR') {
    const error = calculationTactic.error as any; // FIXME: Casting to any as it's a hack for supporting more than just string error
    return error.errorMessage as string;
  }
  return 'Calculation set execution failed.';
}

export function toFailedCalculationSetExecutionMessage(
  calculationTactic: Extract<
    CalculationState['calculationTactic'],
    { current: 'LOADED' }
  >
): string {
  if (calculationTactic.value.status === 'Failed')
    return takeFirstDetailsStatement(calculationTactic.value.details);

  return 'Calculation set execution failed.';
}

export function takeFirstDetailsStatement(details: any): string {
  if (typeof details !== 'string') {
    throw new TypeError('Expected a string as input');
  }
  if (details.includes('.')) return details.split('.')[0];
  return details;
}

export function takeEverythingWithoutFirstDetailsStatement(
  details: any
): string {
  if (typeof details !== 'string') {
    throw new TypeError('Expected a string as input');
  }
  const firstStatement = takeFirstDetailsStatement(details);
  if (details.includes('.')) {
    return details.replace(firstStatement + '.', '').trim();
  }
  return '';
}
export const getErrorWithDetails = (
  calculationSetExecution: CalculationState['calculationTactic']
): ErrorWithDetails => {
  if (calculationSetExecution.current === 'ERROR') {
    return toErrorWithDetailsFromCalculationSetExecutionError(
      calculationSetExecution
    );
  }
  if (
    calculationSetExecution.current === 'LOADED' &&
    calculationSetExecution.value?.status === 'Failed'
  ) {
    return toErrorWithDetailsFromLoadedCalculationSet(calculationSetExecution);
  }
  if (
    calculationSetExecution.value?.status === 'Completed' &&
    calculationSetExecution.value.calculationSetResult.calculationScenarios
      .loadFlow.length > 0 &&
    calculationSetExecution.value.calculationSetResult.calculationScenarios.loadFlow.every(
      scenario => scenario.status === 'Failed'
    )
  ) {
    return { mainMessage: 'Calculation set execution failed.', details: '' };
  }

  return { mainMessage: 'Sorry, Something went wrong.', details: '' };
};

export const getErrorNotificationText = (
  calculationSetExecution: CalculationState['calculationTactic']
) => {
  if (calculationSetExecution.current === 'ERROR')
    return toErrorMessage(calculationSetExecution);
  if (
    calculationSetExecution.current === 'LOADED' &&
    calculationSetExecution.value?.status === 'Failed'
  )
    return toFailedCalculationSetExecutionMessage(calculationSetExecution);
  if (
    calculationSetExecution.value?.status === 'Completed' &&
    calculationSetExecution.value.calculationSetResult.calculationScenarios
      .loadFlow.length > 0 &&
    calculationSetExecution.value.calculationSetResult.calculationScenarios.loadFlow.every(
      scenario => scenario.status === 'Failed'
    )
  )
    return 'Calculation set execution failed.';

  return 'Sorry, Something went wrong.';
};

const showErrorNotification = (context: Context) => {
  const errorNotificationText = getErrorNotificationText(
    context.state.calculation.calculationTactic
  );
  context.state.calculation.calculationTacticNotification = {
    type: 'FAILURE_NOTIFICATION',
    notificationText: errorNotificationText
  };
};

export const hideNotification = (context: Context) => {
  context.state.calculation.calculationTacticNotification = {
    type: 'NO_NOTIFICATION'
  };
};

const showCalculationTacticExecutionFailed = pipe(showErrorNotification);

export const _handleCalculationSetExecutionCompleted = when(
  (context: Context) =>
    context.state.calculation.calculationTactic.current === 'LOADED' &&
    isGridElementSelectedFromMap(context),
  {
    false: noop,
    true: when(
      (context: Context) =>
        isCalculationTacticResultUnavailable(
          context.state.calculation.calculationTactic.value
        ),
      {
        true: showCalculationTacticExecutionFailed,
        false: noop
      }
    )
  }
);

export const toCalculationSetExecutionWithContainment = ([
  containmentResponse,
  calculationResponse
]: [
  PromiseSettledResult<ContainmentResultWithExecutionTime>,
  PromiseSettledResult<CalculationTactic>
]) => {
  if (
    calculationResponse.status === 'fulfilled' &&
    calculationResponse.value.status === 'Completed' &&
    containmentResponse.status === 'fulfilled'
  ) {
    calculationResponse.value.calculationSetResult.grid.containment =
      containmentResponse.value.result.containment;
    calculationResponse.value.calculationSetResult.executionTimes.gettingContainment =
      containmentResponse.value.executionTime;
  }

  if (calculationResponse.status === 'rejected') {
    return calculationResponse.reason.response.data;
  }

  return calculationResponse.value;
};

export const getGridContainment = async (
  context: Context,
  payload: { calculationSetId: GUID, initiatingGridElementId: string, requestController?: AbortController},
) => {
  if(context.state.tenants.currentTenant.type !== 'SELECTED_TENANT') {
    throw new UnexpectedStateError();
  }
  if(context.state.calculation.calculationSets.current !== 'LOADED') {
    throw new UnexpectedStateError();
  }
  
  const tenantId = context.state.tenants.currentTenant.context.tenantId;
  const calculationSet = context.state.calculation.calculationSets.value[payload.calculationSetId];
  const containmentPromise = getContainment(
    tenantId,
    payload.initiatingGridElementId,
    calculationSet,
    context.state.auth.matches('AUTHENTICATED')?.arcgisToken,
    payload.requestController
  );

  const containmentWithExecutionPromise = withExecutionTime(
    () => containmentPromise
  );

  const containment = await containmentWithExecutionPromise;

  return containment
}
export const runCalculation = pipe(
  async (
    { state, effects, actions }: Context,
    { id, tacticId }: RunCalculationV2Action,
    requestController?: AbortController
  ) => {
    if (
      !state.calculation.calculationTactic.matches('LOADING') &&
      state.tenants.currentTenant.type === 'SELECTED_TENANT' &&
      state.calculation.calculationSets.current === 'LOADED'
    ) {
      state.calculation.calculationTactic.send('TRIGGER_LOADING');
      const tenantId = state.tenants.currentTenant.context.tenantId;
      actions.calculation.setCalculationContext({
        calculationContext: {
          calculationSetId: tacticId,
          relatedObjectId: id,
          tenantId
        }
      });

      const containmentWithExecutionPromise = actions.calculation.getGridContainment(
        { calculationSetId: tacticId as unknown as GUID, initiatingGridElementId: id, requestController }
      );

      const calculationSetPromise = effects.api.orchestrator.runCalculation(
        id,
        tenantId,
        tacticId,
        state.auth.matches('AUTHENTICATED')?.arcgisToken
      );

      const evaluationRulesPromise = actions.calculation.getCalculationSetEvaluationRules({ tenantId });

      const promises: [
        Promise<ContainmentResultWithExecutionTime>,
        Promise<CalculationTactic>,
        Promise<void>
      ] = [containmentWithExecutionPromise, calculationSetPromise, evaluationRulesPromise];

      const [containment, execution, _] = await Promise.allSettled(promises);
      return toCalculationSetExecutionWithContainment([containment, execution]);
    } else {
      throw new UnexpectedStateError();
    }
  },
  (context: Context, calculation) => {
    if (context.state.calculation.calculationTactic.matches('LOADING')) {
      context.state.calculation.calculationTactic.send(
        'FETCH_SUCCESS',
        calculation
      );
    }

    return calculation;
  },
  _handleCalculationSetExecutionCompleted,
  catchError(({ state }: Context, error) => {
    if (
      !(error instanceof UnexpectedStateError || error instanceof AbortError)
    ) {
      state.calculation.calculationTactic.send('FETCH_ERROR', error.message);
    }
  })
);

export const setCalculationContext = (
  context: Context,
  { calculationContext }: { calculationContext: CalculationContext }
) => {
  context.state.calculation.calculationContext = {
    type: 'AVAILABLE',
    calculationContext
  };
};

function isGridElementSelectedFromMap(context: Context): boolean {
  return Boolean(context.state.selectedFeature);
}

export function isCalculationTacticResultUnavailable(
  calculationResult:
    | CompletedCalculationTactic
    | FailedCalculationTactic
    | undefined
): calculationResult is FailedCalculationTactic | undefined {
  return (
    !calculationResult ||
    calculationResult.status === 'Failed' ||
    (calculationResult.status === 'Completed' &&
      calculationResult.calculationSetResult.calculationScenarios.loadFlow
        .length > 0 &&
      calculationResult.calculationSetResult.calculationScenarios.loadFlow.every(
        scenario => scenario.status === 'Failed'
      )) ||
    (calculationResult.calculationSetResult.calculationScenarios.shortCircuit
      .length > 0 &&
      calculationResult.calculationSetResult.calculationScenarios.shortCircuit.every(
        scenario => scenario.status === 'Failed'
      ))
  );
}

export const isCalculationSetResultAvailable = compose(
  not,
  isCalculationTacticResultUnavailable
);

export const resetTacticResultBrowsingFilter = ({ state }: Context) => {
  state.calculation.tacticResultBrowsingFilter.type = 'NONE_AVAILABLE';
};

export const unloadCalculation = ({ state, actions }: Context) => {
  state.calculation.calculationTactic.send('UNLOAD');
  state.calculation.resultsBrowsingMode.send('BROWSE_SUMMARY');
  state.calculation.externallyTriggeredCalculationSetExecution.send('RESET');
  actions.calculation.resetTacticResultBrowsingFilter();
};

export type TacticResultBrowsingFilter =
  | { type: 'ALL' }
  | { type: 'ALL_LOAD_FLOW' }
  | { type: 'SELECTED_LOAD_FLOW'; scenarioIds: string[] }
  | { type: 'SHORT_CIRCUIT' }
  | { type: 'NONE_AVAILABLE' };
export const browseTacticResults = (
  context: Context,
  tacticResultBrowsingFilter: TacticResultBrowsingFilter
) => {
  context.state.calculation.previousTacticResultBrowsingFilter = {
    ...context.state.calculation.tacticResultBrowsingFilter
  };
  context.state.calculation.tacticResultBrowsingFilter =
    tacticResultBrowsingFilter;
};

export const handleGridElementSelected = pipe(
  debounce(100), // causes excessive render in component
  async (context: Context, objectId: DomainGuid) => {
    const gridElementType = findGridElementTypeById(
      context.state.arcgis.featureGeometries,
      objectId
    );
    if (gridElementType.type === 'CANNOT_FIND_GRID_ELEMENT_TYPE') return;

    context.state.calculation.resultsBrowsingMode.send(
      'BROWSE_GRID_ELEMENT',
      objectId
    );

    const features = 
    context.state.arcgis.featureGeometries
    .value?.filter(layer =>
      layer.features.find(feature => feature.gc.globalId === objectId)
    )
    .map(layer => layer.features.find(feature => feature.gc.globalId === objectId))
    .filter(feature => feature !== undefined) as FeatureWithIDs[];
    
    if(features?.length === 1) {
      const feature = features[0];
      context.actions.selectedFeature.setSelectedFeature(feature);
    }
  }
);

export const browseResultsSummary = (context: Context) => {
  context.state.calculation.resultsBrowsingMode.send('BROWSE_SUMMARY');
  context.actions.selectedFeature.unselectFeature();
};

export function getAttentionRequiringGridElementsByLoadFlow(
  evaluatedGridElements: GridElementsDetailsByCalculationType['loadFlow'],
  gridElementSections: GridElementSectionWithQuickAccessResult[]
): GridElementSectionWithQuickAccessResult[] {
  return gridElementSections
    .map(section => ({
      ...section,
      gridElements: section.gridElements.filter(elementInSection =>
        evaluatedGridElements.find(
          evaluatedGridElement =>
            evaluatedGridElement.id === elementInSection.objectId
        )
      )
    }))
    .filter(section => section.gridElements.length > 0);
}

export function getAttentionRequiringGridElementsByShortCircuit(
  evaluatedGridElements: GridElementsDetailsByCalculationType['shortCircuit'],
  gridElementSections: GridElementSectionWithQuickAccessResult[]
): GridElementSectionWithQuickAccessResult[] {
  return gridElementSections
    .map(section => ({
      ...section,
      gridElements: section.gridElements.filter(elementInSection =>
        evaluatedGridElements.find(
          evaluatedGridElement =>
            evaluatedGridElement.id === elementInSection.objectId
        )
      )
    }))
    .filter(section => section.gridElements.length > 0);
}

export const loadCalculationSets = pipe(
  async (context: Context) => {
    context.state.calculation.calculationSets.send('TRIGGER_LOADING');
    if (context.state.tenants.currentTenant.type === 'SELECTED_TENANT') {
      const tenantId = context.state.tenants.currentTenant.context.tenantId;

      const calculationSets =
        await context.effects.api.orchestrator.getAllCalculationSets(tenantId);

      context.state.calculation.calculationSets.send(
        'FETCH_SUCCESS',
        calculationSets
      );
    }
  },
  catchError((context: Context, error: Error) => {
    context.state.calculation.calculationSets.send(
      'FETCH_ERROR',
      error.message
    );
  })
);

export type CalculationContext = {
  arcgisToken?: string;
  gridVersionName?: string;
  tenantId: GUID;
  calculationSetId: string;
  relatedObjectId: DomainGuid;
};

export const initializeTriggeringCalculationSetExecution = async (
  context: Context,
  calculationContext?: CalculationContext
) => {
  if (
    !calculationContext ||
    (calculationContext && isEmpty(calculationContext)) ||
    anyRequiredPropertyMissing(calculationContext)
  ) {
    context.state.calculation.externallyTriggeredCalculationSetExecution.send(
      'INVALID_CALCULATION_CONTEXT',
      {
        invalidCalculationContext: calculationContext
      }
    );

    return;
  }

  context.state.calculation.externallyTriggeredCalculationSetExecution.send(
    'INITIALIZE',
    {
      calculationContext,
      calculationSetExecutionResults:
        context.state.calculation.calculationTactic.value
    }
  );
};

export const getCalculationSetEvaluationRules = async (context: Context, payload: {
  tenantId: GUID
}) => {
  const evaluationRules = await context.effects.evaluationRules.provide(payload.tenantId);
  context.state.calculation.evaluationRules = evaluationRules;
}

export const triggerCalculationSetExecution = pipe(
  async (
    context: Context,
    calculationContext: CalculationContext,
    requestController?: AbortController
  ) => {
    context.state.calculation.calculationTactic.send('TRIGGER_LOADING');
    try {
      const calculationResult =
        await context.effects.api.orchestrator.runCalculation(
          calculationContext.relatedObjectId,
          calculationContext.tenantId,
          calculationContext.calculationSetId,
          context.state.auth.matches('AUTHENTICATED')?.arcgisToken,
          calculationContext.gridVersionName,
          requestController
        );
      await context.actions.calculation.ensureContainment({
        calculationContext,
        calculationResult,
        requestController
      })
      context.state.calculation.calculationTactic.send(
        'FETCH_SUCCESS',
        calculationResult
      );
    } catch (error) {
      context.state.calculation.calculationTactic.send('FETCH_ERROR', {
        response: (error as any).response.data,
        errorMessage: takeFirstDetailsStatement(
          (error as any).response.data.details
        ),
        details: takeEverythingWithoutFirstDetailsStatement(
          (error as any).response.data.details
        )
      } as any as string);
    }
  }
);

export const ensureContainment = async (context: Context, payload: { calculationContext: CalculationContext,calculationResult: CalculationTactic, requestController?: AbortController }) => {
  if(!context.state.featureToggles?.shouldUseInitiatingObjectHighlighting) {
    context.effects.log.info(
      'Skipping containment retrieval as feature toggles are not enabled.'
    )
    return
  }
  try {
    if(!isCalculationTacticResultUnavailable(payload.calculationResult) &&
        payload.calculationResult.calculationSetResult.grid.containment.length === 0
      ) {
        const containment = await context.actions.calculation.getGridContainment({
          initiatingGridElementId: payload.calculationContext.relatedObjectId,
          calculationSetId: payload.calculationContext.calculationSetId as any as GUID,
          requestController: payload.requestController
        })
        payload.calculationResult.calculationSetResult.grid.containment = containment.result.containment;
      }
  } catch(error) {
    context.effects.log.info(`Failed to get containment for calculation set execution with due to a problem: ${error}`)
  }
}

export const externallyTriggeredCalculationSetExecutionAuthenticated = (
  context: Context
) => {
  context.state.calculation.externallyTriggeredCalculationSetExecution.send(
    'AUTHENTICATED'
  );
  if (context.state.tenants.availableTenants.type === 'AVAILABLE') {
    context.state.calculation.externallyTriggeredCalculationSetExecution.send(
      'TENANTS_AVAILABLE'
    );
  }
  if (context.state.tenants.currentTenant.type === 'SELECTED_TENANT') {
    context.state.calculation.externallyTriggeredCalculationSetExecution.send(
      'TENANT_SELECTED'
    );
  }
  if (context.state.tenants.hasAccessToApplication.type === 'HAS_ACCESS') {
    context.state.calculation.externallyTriggeredCalculationSetExecution.send(
      'APPLICATION_HAS_ACCESS'
    );
  }
};

export const completeAuthenticationWithExternalArcGisToken = (
  context: Context,
  arcgisToken: string | undefined
) => {
  if (
    arcgisToken &&
    context.state.arcgis.config.current === 'LOADED'
  ) {
    context.effects.arcgis.auth.identity.registerExternalToken(
      context.state.arcgis.config.value,
      arcgisToken
    );
  }
  // @ts-ignore
  context.state.auth.send('TOKEN_AVAILABLE_EXTERNALLY', { arcgisToken });
};

export const resetExternallyTriggeredCalculationSet = (context: Context) => {
  context.state.calculation.externallyTriggeredCalculationSetExecution.send(
    'RESET'
  );
};

export const externallyTriggeredCalculationSetExecutionTenantsAvailable = (
  context: Context
) => {
  context.state.calculation.externallyTriggeredCalculationSetExecution.send(
    'TENANTS_AVAILABLE'
  );
  if (
    context.state.calculation.externallyTriggeredCalculationSetExecution
      .current === 'AWAITING_SELECTED_TENANT'
  ) {
    const { calculationContext } =
      context.state.calculation.externallyTriggeredCalculationSetExecution;
    try {
      context.actions.tenants.selectTenantById(calculationContext.tenantId);
      context.state.calculation.externallyTriggeredCalculationSetExecution.send(
        'TENANT_SELECTED'
      );
    } catch (error) {
      context.state.calculation.externallyTriggeredCalculationSetExecution
        .matches('AWAITING_SELECTED_TENANT')
        ?.send('NO_ACCESS_TO_PROVIDED_TENANT', {
          details: (error as Error).message
        });
    }
  }
};

export const externallyTriggeredCalculationSetExecutionAccessChecked = (
  context: Context,
  hasAccessToApplication: { type: 'HAS_ACCESS' } | { type: 'NO_ACCESS' }
) => {
  if (hasAccessToApplication.type === 'HAS_ACCESS') {
    context.state.calculation.externallyTriggeredCalculationSetExecution.send(
      'APPLICATION_HAS_ACCESS'
    );
  } else {
    context.state.calculation.externallyTriggeredCalculationSetExecution.send(
      'APPLICATION_HAS_NO_ACCESS'
    );
  }
};

export const retryExecutingCalculationSet = (context: Context) => {
  if (
    context.state.calculation.externallyTriggeredCalculationSetExecution
      .current === 'READY'
  ) {
    context.actions.calculation.triggerCalculationSetExecution(
      Object.assign(
        {},
        context.state.calculation.externallyTriggeredCalculationSetExecution
          .calculationContext
      )
    );
  }
};

const requiredProperties = ['calculationSetId', 'tenantId', 'relatedObjectId'];
function anyRequiredPropertyMissing(
  calculationContext: CalculationContext
): boolean {
  return (
    requiredProperties.filter(prop => !(prop in calculationContext)).length > 0
  );
}

function isEmpty(calculationContext: CalculationContext) {
  return Object.keys(calculationContext).length === 0;
}

export const shareCalculationSetExecution = (context: Context) => {
  if (context.state.calculation.calculationContext.type === 'NOT_AVAILABLE')
    return;
  const { calculationContext } = context.state.calculation.calculationContext;
  context.effects.sharing.shareCalculationSetExecution(calculationContext);
  context.state.calculation.calculationTacticNotification = {
    type: 'SUCCESSFUL_NOTIFICATION',
    notificationText: 'URL to calculation set execution copied!'
  };
};

export const saveCalculationSetExecutionReport = async (context: Context) => {
  if (
    !context.state.featureToggles?.isSavingCalculationSetExecutionReportEnabled
  )
    return;
  await context.effects.calculationSetExecutionReport.save();
};

type GridElementTypeResult =
  | { type: 'GRID_ELEMENT_TYPE'; data: GridElementType }
  | { type: 'CANNOT_FIND_GRID_ELEMENT_TYPE' };
function findGridElementTypeById(
  featureGeometries: Context['state']['arcgis']['featureGeometries'],
  objectId: DomainGuid
): GridElementTypeResult {
  if (featureGeometries.current !== 'LOADED')
    return { type: 'CANNOT_FIND_GRID_ELEMENT_TYPE' };
  const layer = featureGeometries.value.find(layer =>
    layer.features.find(feature => feature.gc.globalId === objectId)
  );
  if (layer === undefined) return { type: 'CANNOT_FIND_GRID_ELEMENT_TYPE' };

  return {
    type: 'GRID_ELEMENT_TYPE',
    data: GridElementType.from(layer.layerId)
  };
}

export const createGridElementPropertyAttentionStatuses = (
  context: Context,
  statuses: GridElementPropertyAttentionStatusByCalculationType
) => {
  // TODO: this ia s temporary solution, should be refactored. Should this state should derived state and gridElementDetailsByCalculationType and others should use it. Not evaluate by itself?
  context.state.calculation.gridElementsAttentionStatuses = {
    type: 'AVAILABLE',
    byCalculationType: statuses
  };
};
